import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UseSharedUsersProps {
  assetId?: number;
  placeId?: number;
  companyId?: number;
}

export function useSharedUsers({ assetId, placeId, companyId }: UseSharedUsersProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['shared-users', { assetId, placeId, companyId }];

  const { data: sharedUsers, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('shared_assets')
        .select(`
          id,
          shared_with,
          shared_at,
          profiles (
            email
          )
        `)
        .order('shared_at', { ascending: false });

      if (assetId) {
        query = query.eq('asset_id', assetId);
      } else if (placeId) {
        query = query.eq('place_id', placeId);
      } else if (companyId) {
        query = query.eq('company_id', companyId)
          .eq('shared_type', 'company');
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching shared users:', error);
        toast({
          variant: "destructive",
          title: "Error fetching shared users",
          description: error.message,
        });
        throw error;
      }
      
      // Transform the data to match the expected format
      return data?.map(share => ({
        ...share,
        shared_with_profile: {
          email: share.profiles?.email
        }
      })) || [];
    },
  });

  const revokeAccess = useMutation({
    mutationFn: async (sharedWithId: string) => {
      const { error } = await supabase
        .from('shared_assets')
        .delete()
        .match({
          shared_with: sharedWithId,
          ...(assetId ? { asset_id: assetId } : {}),
          ...(placeId ? { place_id: placeId } : {}),
          ...(companyId ? { company_id: companyId, shared_type: 'company' } : {}),
        });

      if (error) {
        console.error('Error revoking access:', error);
        toast({
          variant: "destructive",
          title: "Error revoking access",
          description: error.message,
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Access revoked successfully"
      });
    },
  });

  return {
    sharedUsers,
    isLoading,
    revokeAccess,
  };
}